import * as m from '@staiter/i18n';
import type { CounterpartyKind } from '@staiter/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Breadcrumbs, PageHeader } from '~/components/data';
import { useToast } from '~/components/feedback';
import { Button, Input, Select } from '~/components/primitives';
import {
  type CounterpartyCreateBody,
  fetchCounterparty,
  updateCounterparty,
} from '~/lib/api/counterparties';
import { renderApiErrorMessage } from '~/lib/api/error-renderer';
import shared from './_auth._shared.module.css';

/**
 * EFE-1 Pass 88 (2026-05-25) — Counterparty edit page.
 * Mirrors `_auth.counterparties.new.tsx` shape, prefilled from `fetchCounterparty`.
 * Wires `updateCounterparty(id, body)` (existed unused before).
 */

export const Route = createFileRoute('/_auth/counterparties/$id/edit')({
  component: CpEdit,
});

function CpEdit(): JSX.Element {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['counterparties', id],
    queryFn: () => fetchCounterparty(id),
  });

  const [form, setForm] = useState({
    kind: 'customer' as CounterpartyKind,
    name_ru: '',
    bin_iin: '',
    tax_status: 'vat_payer' as 'vat_payer' | 'simplified' | 'patent' | 'individual',
    default_currency: 'KZT' as 'KZT' | 'USD' | 'EUR' | 'RUB' | 'CNY',
    payment_terms_days: 30,
  });
  const [primed, setPrimed] = useState(false);

  useEffect(() => {
    if (query.data && !primed) {
      const cp = query.data;
      setForm({
        kind: cp.kind as CounterpartyKind,
        name_ru: cp.name_ru ?? '',
        bin_iin: cp.bin_iin ?? '',
        tax_status: cp.tax_status as 'vat_payer' | 'simplified' | 'patent' | 'individual',
        default_currency: (cp.default_currency ?? 'KZT') as 'KZT' | 'USD' | 'EUR' | 'RUB' | 'CNY',
        payment_terms_days: cp.payment_terms_days ?? 30,
      });
      setPrimed(true);
    }
  }, [query.data, primed]);

  const isIndividual = form.tax_status === 'individual' || form.tax_status === 'patent';
  const binIinLabel = isIndividual ? m.cp_column_iin() : m.cp_column_bin();
  const showTaxStatus = form.kind !== 'other';

  const updateMut = useMutation({
    mutationFn: () => {
      const body: Partial<CounterpartyCreateBody> = {
        kind: form.kind,
        name_ru: form.name_ru,
        bin_iin: form.bin_iin || null,
        tax_status: form.tax_status,
        payment_terms_days: form.payment_terms_days,
        default_currency: form.default_currency,
      };
      return updateCounterparty(id, body);
    },
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.cp_toast_updated() });
      void qc.invalidateQueries({ queryKey: ['counterparties', id] });
      void qc.invalidateQueries({ queryKey: ['counterparties'] });
      void navigate({ to: '/counterparties/$id', params: { id } });
    },
    onError: (err: unknown) =>
      toast.push({
        variant: 'error',
        title: m.cp_toast_update_failed(),
        description: renderApiErrorMessage(err),
      }),
  });

  if (query.isLoading) return <div>…</div>;
  if (!query.data) return <div>Not found</div>;

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: m.phase2_counterparties_title(), to: '/counterparties' },
              { label: query.data.name_ru, to: `/counterparties/${id}` },
              { label: m.common_button_edit() },
            ]}
          />
        }
        title={`${m.common_button_edit()}: ${query.data.name_ru}`}
      />
      <div className={shared.formColumn480}>
        <Select
          label={m.cp_column_kind()}
          required
          value={form.kind}
          onChange={(e) => setForm({ ...form, kind: e.target.value as CounterpartyKind })}
          options={[
            { value: 'customer', label: m.cp_kind_customer() },
            { value: 'supplier', label: m.cp_kind_supplier() },
            { value: 'employee', label: m.cp_kind_employee() },
            { value: 'other', label: m.cp_kind_other() },
          ]}
        />
        <Input
          label={m.cp_column_name()}
          required
          value={form.name_ru}
          onChange={(e) => setForm({ ...form, name_ru: e.target.value })}
        />
        <Input
          label={binIinLabel}
          value={form.bin_iin}
          onChange={(e) => setForm({ ...form, bin_iin: e.target.value })}
          placeholder={m.cp_bin_iin_placeholder()}
          hint={m.cp_bin_iin_hint()}
          inputMode="numeric"
          maxLength={12}
        />
        {showTaxStatus && (
          <Select
            label={m.cp_column_tax_status()}
            required
            value={form.tax_status}
            onChange={(e) =>
              setForm({
                ...form,
                tax_status: e.target.value as typeof form.tax_status,
              })
            }
            options={[
              { value: 'vat_payer', label: m.cp_tax_vat_payer() },
              { value: 'simplified', label: m.cp_tax_simplified() },
              { value: 'patent', label: m.cp_tax_patent() },
              { value: 'individual', label: m.cp_tax_individual() },
            ]}
          />
        )}
        <Select
          label={m.cp_column_currency()}
          required
          value={form.default_currency}
          onChange={(e) =>
            setForm({
              ...form,
              default_currency: e.target.value as typeof form.default_currency,
            })
          }
          options={[
            { value: 'KZT', label: 'KZT' },
            { value: 'USD', label: 'USD' },
            { value: 'EUR', label: 'EUR' },
            { value: 'RUB', label: 'RUB' },
            { value: 'CNY', label: 'CNY' },
          ]}
        />
      </div>
      <div className={shared.stickyFooter}>
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate({ to: '/counterparties/$id', params: { id } })}
        >
          {m.common_button_cancel()}
        </Button>
        <Button
          variant="primary"
          onClick={() => updateMut.mutate()}
          loading={updateMut.isPending}
          disabled={!form.name_ru}
        >
          {m.common_button_save()}
        </Button>
      </div>
    </div>
  );
}
